/**
 * The chat dock's handoff slot: one prompt, parked by an editor host, picked up
 * by whichever chat body the dock currently has mounted.
 *
 * The dock already decouples editors from chats — only the ACTIVE surface's body
 * is mounted (`DockSlot`), and the registry guarantees exactly one of those at a
 * time — so "which chat receives this?" needs no arbitration here. What the slot
 * owes is the two guarantees that fail silently if skipped: a prompt is consumed
 * at most once, and a prompt nobody picked up expires instead of surfacing later
 * in an unrelated conversation.
 *
 * The prompt is an opaque string. The dock deliberately knows nothing about the
 * dictionary lookup that produced it (see `editor/cm6/lookupHandoffPrompt.ts`),
 * so any future host can hand off any question.
 */

import { useCallback, useEffect, useRef } from 'react';

import { create } from 'zustand';

/**
 * How long a parked prompt stays deliverable, in milliseconds.
 *
 * A handoff is the direct consequence of a click, and its consumer is either
 * already mounted (the dock body of the surface the user is on) or mounts within
 * a render or two — so the real delivery window is well under a second. The
 * generous headroom covers the one slow path, a body whose `onChatReady` waits
 * on a conversation-history fetch, without ever reaching the failure this guard
 * exists for: the user closing the dock, wandering off, and finding a question
 * about a word they've forgotten fired into some other chat minutes later.
 */
export const MAX_HANDOFF_AGE_MS = 30_000;

/** A prompt waiting for a chat body to pick it up. */
export interface PendingChatHandoff {
  /** The fully composed message to send, verbatim. */
  prompt: string;
  /** Epoch ms the handoff was requested, used to expire it at consume time. */
  requestedAt: number;
}

interface ChatHandoffState {
  /** The single parked prompt, or null when nothing is waiting. */
  pending: PendingChatHandoff | null;
  /**
   * Park `prompt` for the mounted chat body, replacing any earlier one.
   *
   * The caller is responsible for opening the dock (`expand()`); parking alone
   * delivers nothing. `now` is injectable so tests need no clock control.
   */
  request: (prompt: string, now?: number) => void;
  /**
   * Take the parked prompt, or null when there is none or it has expired.
   *
   * Clears the slot either way, so a stale prompt is discarded on the first look
   * rather than left to be re-evaluated forever. Synchronous read-and-clear is
   * what makes "exactly once" hold when two bodies overlap during a surface
   * switch: the second caller finds an empty slot.
   */
  consume: (now?: number) => string | null;
  /** Abandon any parked prompt without sending it. */
  clear: () => void;
}

/** Store for the dock's single pending chat handoff. */
export const useChatHandoffStore = create<ChatHandoffState>((set, get) => ({
  pending: null,
  request: (prompt, now = Date.now()) => set({ pending: { prompt, requestedAt: now } }),
  consume: (now = Date.now()) => {
    const pending = get().pending;
    if (!pending) return null;
    set({ pending: null });
    if (now - pending.requestedAt > MAX_HANDOFF_AGE_MS) return null;
    return pending.prompt;
  },
  clear: () => set({ pending: null }),
}));

/** The slice of a chat engine's ready-api a handoff needs. */
interface ChatHandoffTarget {
  /**
   * Put `value` in the composer, for the writer to read, edit and send.
   *
   * Takes an updater as well as a value — this IS the composer's `useState`
   * dispatch — so a handoff can be placed ABOVE a draft instead of erasing it.
   */
  setInput: (value: string | ((previous: string) => string)) => void;
}

/**
 * Place `prompt` above whatever is already in the composer.
 *
 * A handoff is context for a question, so it belongs before the question: the
 * quote or the looked-up word first, then the half-typed thought it was meant
 * to inform. Replacing the draft — which is what this used to do — throws away
 * the only thing in the exchange the writer actually wrote.
 *
 * The prompt's own trailing blank line is trimmed first so the join is one
 * blank line rather than three: `composeSelectionPrompt` ends with `\n\n` when
 * it carries no question of its own.
 */
export function placeAboveDraft(prompt: string, draft: string): string {
  if (draft.trim() === '') return prompt;
  return `${prompt.replace(/\s+$/, '')}\n\n${draft}`;
}

/**
 * Wire a mounted chat body to the handoff slot.
 *
 * Returns a stable `onChatReady` callback to hand straight to the chat engine.
 * Delivery has to cover both orders, which is why readiness alone is not enough:
 * a keep-mounted dock body reported ready long before the user ever selected a
 * word, so a prompt parked afterwards would sit there unseen. Subscribing to the
 * slot catches that case; draining on ready catches the body that mounts second.
 *
 * **The prompt lands in the composer above any draft; it is never sent.** The earlier version
 * called `handleSend`, which resolves successfully even when the underlying
 * `engine.send` rejects the send by RETURNING `{ ok: false, reason }` — and
 * `'no-conversation'` is precisely the state the dock is in for the first
 * moments after it opens. The prompt was consumed, the rejection discarded, and
 * the writer saw an empty chat with no sign anything had happened.
 *
 * Writing to the composer removes the failure class rather than papering over
 * it: a synchronous state update cannot half-succeed, there is no conversation
 * id to race, and the writer sees the question before it is asked. That last
 * part is the point — the question is theirs, and it should be editable before
 * it goes.
 *
 * @returns The engine's `onChatReady` handler, stable across renders.
 */
export function useChatHandoffReceiver(): (api: ChatHandoffTarget) => void {
  const setInputRef = useRef<ChatHandoffTarget['setInput'] | null>(null);

  // Consume only when there is somewhere to put the prompt: taking it before
  // the body is ready would drop it on the floor.
  const drain = useCallback((): void => {
    const setInput = setInputRef.current;
    if (!setInput) return;
    const prompt = useChatHandoffStore.getState().consume();
    if (prompt !== null) setInput(draft => placeAboveDraft(prompt, draft));
  }, []);

  useEffect(() => useChatHandoffStore.subscribe(drain), [drain]);

  return useCallback(
    (api: ChatHandoffTarget): void => {
      setInputRef.current = api.setInput;
      drain();
    },
    [drain],
  );
}
